import { Caption, Grid, InputRadio } from '@design-systems/canvas-ui'
import { AccountCard } from './AccountCard'
import { PersonGroupIcon } from '@design-systems/react-icons'
import { useController, type Control } from 'react-hook-form'
import { useContext, useMemo } from 'react'
import { UserContext } from 'App/UserContext'
import {
  BP_ROLE_DESCRIPTION,
  BP_ROLE_TYPE_LOOKUP_TABLE,
  PLM_SUPPORTED_ROLE_TYPES,
} from 'config/BusinessPartnerConsts'
import { AccountSettingsInputs } from 'account/types'

type Props = {
  control: Control<AccountSettingsInputs>
}

export const PartnerTypeCard = ({ control }: Props) => {
  const { sparkUser } = useContext(UserContext)

  const { field } = useController({ control, name: 'defaultPartnerType' })

  const partnerTypeOptions = useMemo(() => {
    const approvedVMMRoles = sparkUser?.approvedVMMRoles ?? []
    return PLM_SUPPORTED_ROLE_TYPES.filter((roleType) =>
      approvedVMMRoles.includes(roleType),
    ).map((roleType) => ({
      value: String(roleType),
      label:
        BP_ROLE_TYPE_LOOKUP_TABLE[
          roleType as keyof typeof BP_ROLE_TYPE_LOOKUP_TABLE
        ],
      description:
        BP_ROLE_DESCRIPTION[roleType as keyof typeof BP_ROLE_DESCRIPTION],
    }))
  }, [sparkUser?.approvedVMMRoles])

  if (partnerTypeOptions.length < 2) return null

  return (
    <AccountCard
      svg={PersonGroupIcon}
      cardHeading="Partner type"
      cardSubheading="Choose the partner type you want to use in PLM."
    >
      <Grid.Container spacing="expanded">
        <Grid.Item size={{ xs: 12, sm: 6 }}>
          <Caption below="This selection should be based on your primary focus. Changing partner type may change what information and actions are available.">
            <strong>Set partner type</strong>
          </Caption>
        </Grid.Item>
        <Grid.Item size={{ xs: 12 }}>
          <Grid.Container direction="column" spacing="dense">
            {partnerTypeOptions.map((option) => (
              <Grid.Item key={option.value}>
                <Caption below={option.description}>
                  <InputRadio
                    id={`defaultPartnerType-${option.value}`}
                    name={field.name}
                    value={option.value}
                    label={option.label}
                    isChecked={field.value === option.value}
                    onChange={() => field.onChange(option.value)}
                    onBlur={field.onBlur}
                  />
                </Caption>
              </Grid.Item>
            ))}
          </Grid.Container>
        </Grid.Item>
      </Grid.Container>
    </AccountCard>
  )
}

export default PartnerTypeCard
